<template lang="pug">
  CommonSection.b-supply-page(
    title="Ведомость поставки"
    data-testid="supply-page"
    :back="{ title: project && project.name, name: 'project-stages', params: { projectId } }"
    )
    ._toolbar(v-if="specification")
      label._hide
        input(type="checkbox" data-testid="supply-hide-accepted" v-model="hideAccepted" @change="load")
        |
        | Скрыть принятые
      ._export
        Btn(grey sm data-testid="supply-export" @click="exportOpen = !exportOpen") Экспорт ▾
        SpecPopover(v-if="exportOpen")
          a.spec-popover-item(data-testid="supply-export-xlsx" :href="exportUrl('xlsx')" @click="exportOpen = false") Ведомость, xlsx
          a.spec-popover-item(data-testid="supply-export-pdf" :href="exportUrl('pdf')" @click="exportOpen = false") Ведомость, pdf

    ._empty(v-if="!groups.length" data-testid="supply-empty") Позиций в закупке пока нет

    ._group(v-for="group in groups" :key="group.supplier.id" data-testid="supply-group")
      ._group-head
        span._supplier(data-testid="supply-group-supplier") {{ group.supplier.name }}
        span._contacts(data-testid="supply-group-contacts") {{ contacts(group.supplier) }}
        button._link(type="button" data-testid="supply-group-chat" @click="goToChat(group)") Чат с поставщиком
        ._export
          button._link(type="button" data-testid="supply-group-export" @click="toggleGroupExport(group)") Выгрузить по поставщику ▾
          SpecPopover(v-if="groupExportId === group.supplier.id")
            a.spec-popover-item(data-testid="supply-group-export-xlsx" :href="exportUrl('xlsx', group.supplier.id)" @click="groupExportId = null") По поставщику, xlsx
            a.spec-popover-item(data-testid="supply-group-export-pdf" :href="exportUrl('pdf', group.supplier.id)" @click="groupExportId = null") По поставщику, pdf
      ._group-totals
        span._total
          span Сумма счетов заказчику
          b(data-testid="supply-group-invoices") {{ money(group.invoicesTotal) }}
        span._total
          span Вознаграждение начислено
          b(data-testid="supply-group-reward-accrued") {{ money(group.rewardAccrued) }}
        span._total
          span Получено
          b(data-testid="supply-group-reward-received") {{ money(group.rewardReceived) }}
        span._total
          span Ожидается
          b(data-testid="supply-group-reward-expected") {{ money(group.rewardExpected) }}

      table._table
        thead
          tr
            th Наименование
            th Кол-во
            th Цена для заказчика
            th Сумма
            th Вознаграждение
            th Получено
            th Срок
            th Статус
            th
        tbody
          tr._row(v-for="row in group.items" :key="row.id" data-testid="supply-row" :class="{ readonly: row.status === 'accepted' }")
            td(data-testid="supply-row-name")
              | {{ row.name }}
              span._readonly-mark(v-if="row.status === 'accepted'" data-testid="supply-row-readonly" title="Позиция принята: только чтение") ●
            td(data-testid="supply-row-quantity") {{ quantity(row.quantity) }} {{ unitTitle(row.unit) }}
            td(data-testid="supply-row-customer-price") {{ money(row.customerPrice) }}
            td(data-testid="supply-row-customer-total") {{ money(row.customerTotal) }}
            td(data-testid="supply-row-reward") {{ money(row.designerRewardTotal) }}
            td._reward
              input._check(
                v-if="canMarkReward(row)"
                type="checkbox"
                data-testid="supply-row-reward-received"
                :checked="!!row.rewardReceivedAt"
                @change="markReward(row, $event.target.checked)"
                )
              span._date(v-if="row.rewardReceivedAt" data-testid="supply-row-reward-received-date") {{ date(row.rewardReceivedAt) }}
              input._date-input(
                v-if="canMarkReward(row)"
                type="date"
                data-testid="supply-row-reward-received-date-input"
                title="Дата получения вознаграждения"
                :value="dateValue(row)"
                @change="onRewardDate(row, $event.target.value)"
                )
            td(data-testid="supply-row-delivery") {{ deliveryText(row.deliveryTerm, '—') }}
            td(data-testid="supply-row-status") {{ itemStatusTitle(row.status) }}
            td
              Btn(v-if="!readOnly && nextOf(row)" blue xs data-testid="supply-row-next" @click="next(row)") {{ itemStatusTitle(nextOf(row)) }}
</template>

<script>
import CommonSection from '@/components/CommonSection.vue';
import SpecPopover from '@/components/specification/SpecPopover.vue';
import specificationApi from '@/api/specification';
import model from '@/model/specification';
import {
  money, quantity, date, deliveryText, unitTitle, itemStatusTitle, SUPPLY_NEXT,
} from '@/utils/spec';

/**
 * Вкладка «Ведомость поставки» (ТЗ 6.3): группы по поставщику с контактами и итогами (7.4),
 * позиции в статусе «Заказано» и далее, отметка «вознаграждение получено» с выбором даты и
 * кнопка следующего шага закупки. Все суммы приходят с сервера.
 *
 * Спецификацию берём из хранилища: её идентификатор уже прочитан вкладкой «Спецификация», и
 * перечитывать позиции ради него не нужно. В архивном проекте — только чтение (ТЗ 6.1).
 * Сгенерировано Claude.
 */
export default {
  name: 'SupplyPage',
  components: { CommonSection, SpecPopover },
  props: {
    project: Object,
  },
  data() {
    return {
      specification: null,
      supply: null,
      hideAccepted: false,
      exportOpen: false,
      /** Поставщик, у которого открыто меню выгрузки. */
      groupExportId: null,
      /** Выбранные дизайнером даты получения вознаграждения по позициям (ТЗ 6.3). */
      dates: {},
    };
  },
  computed: {
    projectId() { return Number(this.$route.params.projectId); },
    groups() { return this.supply?.suppliers ?? []; },
    readOnly() { return !!this.project?.isArchived; },
  },
  watch: {
    projectId: {
      immediate: true,
      handler() { this.init(); },
    },
  },
  methods: {
    money,
    quantity,
    date,
    deliveryText,
    unitTitle,
    itemStatusTitle,
    async init() {
      this.specification = await model.ensure(this.projectId);
      await this.load();
    },
    toggleGroupExport(group) {
      const { id } = group.supplier;
      this.groupExportId = this.groupExportId === id ? null : id;
    },
    load() {
      if (!this.specification) return Promise.resolve();
      return specificationApi.fetchSupply(this.specification.id, this.hideAccepted)
        .then((supply) => { this.supply = supply; });
    },
    contacts(supplier) {
      return [supplier.contactPerson, supplier.phone, supplier.email, supplier.address]
        .filter((value) => !!value)
        .join(' · ');
    },
    nextOf(row) { return SUPPLY_NEXT[row.status] ?? null; },
    /** Отметка вознаграждения доступна со статуса «Оплачено» (ТЗ 4.1, 6.3). */
    canMarkReward(row) {
      return !this.readOnly && ['paid', 'delivered', 'accepted'].includes(row.status);
    },
    async next(row) {
      const action = this.nextOf(row);
      if (!action) return;
      const updated = await model.transition(row, action);
      if (!updated) return;
      await this.load();
    },
    /**
     * Дата в поле выбора (ТЗ 6.3 «чекбокс и дата»): выбранная дизайнером, иначе уже отмеченная,
     * иначе сегодняшняя.
     */
    dateValue(row) {
      if (this.dates[row.id]) return this.dates[row.id];
      return row.rewardReceivedAt ? String(row.rewardReceivedAt).slice(0, 10) : this.today();
    },
    today() {
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    },
    async markReward(row, value) {
      const updated = await model.rewardReceived(row.id, value ? this.dateValue(row) : null);
      if (!updated) return;
      await this.load();
    },
    /**
     * Выбор даты: до отметки просто запоминается (с ней уйдёт отметка), у уже отмеченного
     * вознаграждения сразу сохраняется (ТЗ 6.3).
     */
    async onRewardDate(row, value) {
      if (!value) return;
      this.dates = { ...this.dates, [row.id]: value };
      if (!row.rewardReceivedAt) return;
      const updated = await model.rewardReceived(row.id, value);
      if (!updated) return;
      await this.load();
    },
    goToChat(group) {
      this.$router.push({
        name: group.chatId ? 'project-chat' : 'project-chats',
        params: { projectId: this.projectId, chatId: group.chatId },
      });
    },
    exportUrl(format, supplier = null) {
      if (!this.specification) return '#';
      return specificationApi.supplyExportUrl(this.specification.id, {
        format,
        supplier,
        hideAccepted: this.hideAccepted,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.supply-page {
  &__toolbar {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 16px;
  }

  &__hide {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    cursor: pointer;
  }

  &__export {
    position: relative;
  }

  &__empty {
    padding: 30px 0;
    color: var(--color-middle-gray);
  }

  &__group {
    margin-bottom: 26px;
    border: 1px solid var(--color-super-light);
    border-radius: 8px;
    background: var(--color-white);
    overflow: hidden;
  }

  &__group-head {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 14px;
    padding: 12px 16px;
    background: #EEF3FA;
  }

  &__supplier {
    font-weight: 700;
    font-size: 16px;
  }

  &__contacts {
    font-size: 12px;
    color: var(--color-middle-gray);
  }

  &__link {
    border: none;
    background: none;
    font: inherit;
    font-size: 13px;
    color: var(--color-accent);
    cursor: pointer;
    padding: 0;
  }

  &__group-totals {
    display: flex;
    flex-wrap: wrap;
    gap: 18px;
    padding: 10px 16px;
    font-size: 12px;
    color: var(--color-middle-gray);

    b {
      color: var(--color-dark-gray);
    }
  }

  &__total {
    display: inline-flex;
    gap: 6px;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th,
    td {
      text-align: left;
      padding: 8px 12px;
      border-top: 1px solid var(--color-super-light);
    }

    th {
      color: var(--color-middle-gray);
      font-weight: 400;
      white-space: nowrap;
    }
  }

  &__row {
    &.readonly {
      background: #F5F6F8;
      color: var(--color-light-dark);
    }
  }

  &__readonly-mark {
    margin-left: 6px;
    color: var(--color-light-dark);
  }

  &__check {
    width: 16px;
    height: 16px;
    margin: 0;
  }

  &__reward {
    white-space: nowrap;
  }

  &__date {
    margin-left: 6px;
    white-space: nowrap;
  }

  &__date-input {
    display: block;
    margin-top: 4px;
    padding: 2px 4px;
    border: 1px solid var(--color-super-light);
    border-radius: 4px;
    font: inherit;
    font-size: 11px;
  }
}
</style>
